import React, { useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import Select from "react-select";
import { MdOutlineDeleteOutline } from "react-icons/md";

const StackedVisualization = ({
    indicators,
    locations,
    onSubmit, // Handler from parent
    // Receive all states as props from parent (NO MORE useState!)
    localSelectedLocations,
    setLocalSelectedLocations,
    stacked_chartType,
    setStacked_chartType,
    stackedIndicators,
    setStackedIndicators
}) => {
    // Redux selector for stacked config (for restoration only)
    const stackedConfig = useSelector((state) => state.mapConfig.stackedConfig);

    // Helper function for geo level labels
    const geoLevelLabel = (level) => {
        switch (level) {
            case 1: return "National";
            case 2: return "State";
            case 3: return "District";
            case 4: return "ULG";
            default: return "Unknown";
        }
    };

    const noOptionsMessage = () => "No compatible indicators found in your workspace.";

    useEffect(() => {
        if (!locations || locations.length === 0) return;

        const validOptions = [
            {
                label: `All States / UTs of India`,
                value: 1000, geo_level: 1, type: "Children"
            },
            ...locations.map(f => ({
                value: f.properties?.code,
                label: `${f.name || f.properties?.name} (${geoLevelLabel(f.geo_level_code || f.geo_level || f.properties?.geo_level_code || 4)})`,
                geo_level: f.geo_level_code || f.geo_level || f.properties?.geo_level_code || 4,
                type: ""
            }))
        ];

        // Filter out any selected locations that no longer exist
        const filteredSelected = localSelectedLocations.filter(selected =>
            validOptions.some(opt => opt.value === selected.value && opt.type === selected.type)
        );

        if (filteredSelected.length !== localSelectedLocations.length) {
            setLocalSelectedLocations(filteredSelected);
        }
    }, [locations]);

    // Handle stacked indicator change
    const handleStackedIndicatorChange = (index, selectedOption) => {
        setStackedIndicators((prev) => {
            const newIndicators = [...prev];
            if (!newIndicators[index]) {
                newIndicators[index] = { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null };
            }
            newIndicators[index].colorIndicator = selectedOption;
            newIndicators[index].colorSubIndicators = selectedOption?.subIndicators || [];
            newIndicators[index].selectedColorSubIndicator = null;
            return newIndicators;
        });
    };

    // Handle sub indicator change
    const handleSubIndicatorChange = (index, selectedOption) => {
        setStackedIndicators((prev) => {
            const newIndicators = [...prev];
            newIndicators[index].selectedColorSubIndicator = selectedOption;
            return newIndicators;
        });
    };

    // Add indicator (max 4)
    const handleAddStackedIndicator = () => {
        setStackedIndicators((prev) => {
            if (prev.length >= 1) {
                return prev;
            }
            return [
                ...prev,
                { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null },
            ];
        });
    };

    // Remove indicator by index
    const handleRemoveStackedIndicator = (idx) => {
        setStackedIndicators((prev) => prev.filter((_, i) => i !== idx));
    };

    // Reset all indicators
    const handleStackedReset = () => {
        setStackedIndicators([
            { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null },
        ]);
        setLocalSelectedLocations([]);
        setStacked_chartType('column');
    };

    // Data patch/restoration logic using useEffect to read from Redux stackedConfig
    useEffect(() => {
        if (!stackedConfig) return;
        if (stackedConfig?.indicators?.length > 0) {
            if (stackedConfig.selectedLocations) {
                setLocalSelectedLocations(stackedConfig.selectedLocations);
            }
        }
        if (stackedConfig.stacked_chartType) {
            setStacked_chartType(stackedConfig.stacked_chartType);
        }

        const patchedIndicators = stackedConfig.indicators.map((ind) => {
            const matched = indicators.find((f) => f.indicator_code === ind);
            return {
                colorIndicator: matched
                    ? {
                        value: matched.indicator_code,
                        label: matched.indicator_name,
                        subIndicators: matched.sub_indicators || [],
                    }
                    : { value: ind, label: ind },
                colorSubIndicators: matched?.sub_indicators || [],
                selectedColorSubIndicator: null,
            };
        });

        setStackedIndicators(patchedIndicators);
    }, [stackedConfig, indicators]);

    // Submit functionality - NO API CALL, just prepare data and call parent handler
    const handleStackedSubmit = () => {
        const lgdDatas = localSelectedLocations.map((item) => ({
            code: item.value,
            geo_level_code: item.geo_level,
            type: item.type || ""
        }));

        const formattedIndicators = stackedIndicators.map(
            (ind) => ind.colorIndicator?.value || ""
        );

        const payload = {
            lgd_codes: lgdDatas,
            indicators: formattedIndicators,
            chartType: stacked_chartType
        };

        const formattedPayload = {
            location_info: localSelectedLocations,
            indicator_info: indicators.reduce((acc, cur) => {
                acc[cur.indicator_code] = cur;
                return acc;
            }, {}),
            chartType: stacked_chartType
        };

        const reduxPayload = {
            visualizationType: "stacked",
            selectedLocations: localSelectedLocations,
            indicators: formattedIndicators,
            stacked_chartType: stacked_chartType
        };

        // Call parent submit handler with prepared data
        onSubmit({ payload, formattedPayload, reduxPayload });
    };

    // Check if submit should be disabled
    const isStackedSubmitDisabled = !(
        stackedIndicators.some(ind => ind.colorIndicator) &&
        Array.isArray(localSelectedLocations) &&
        localSelectedLocations.length > 0
    );

    return (
        <div className="col-md-12 mt-3">
            <div className="cardbg p-3">
                <Form.Label>Stacked Chart Configuration</Form.Label>
                <div className="col-md-6">
                    {/* Chart Type (Radio buttons) */}
                    <Form.Group className="mb-3">
                        <div>
                            <Form.Check
                                inline
                                type="radio"
                                label="Column"
                                name="stackedChartType"
                                value="column"
                                checked={stacked_chartType === "column"}
                                onChange={(e) => setStacked_chartType(e.target.value)}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                label="Bar"
                                name="stackedChartType"
                                value="bar"
                                checked={stacked_chartType === "bar"}
                                onChange={(e) => setStacked_chartType(e.target.value)}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Location</Form.Label>
                        <Select
                            isMulti
                            options={[
                                {
                                    label: `All States / UTs of India`,
                                    value: 1000, geo_level: 1, type: "Children"
                                },
                                ...(locations.length > 0
                                    ? locations.flatMap((f) => {
                                        const name = f.name || f.properties?.name;
                                        const code = f.properties?.code || f.id;
                                        const geoLevel = f.properties?.geo_level_code;
                                        const type = "Children"
                                        if (geoLevel === 2) {
                                            return [
                                                {
                                                    label: `All Districts of ${name}`,
                                                    value: code,
                                                    geo_level: 2,
                                                    type
                                                },
                                            ];
                                        }
                                        if (geoLevel === 3) {
                                            return [
                                                {
                                                    label: `All ULG of ${name}`,
                                                    value: code,
                                                    geo_level: 3,
                                                    type
                                                },
                                            ];
                                        }
                                        return [];
                                    })
                                    : []),
                                {
                                    label: "India (National)",
                                    value: 1000, geo_level: 1
                                },
                                ...locations.map((f) => ({
                                    value: f.properties?.code || f.id,
                                    label: `${f.name || f.properties?.name} (${geoLevelLabel(f.properties?.geo_level_code || f.geo_level || 4)})`,
                                    geo_level: f.properties?.geo_level_code || f.geo_level || f.geo_level_code,
                                    type: ""
                                })),
                            ]}
                            onChange={setLocalSelectedLocations}
                            value={localSelectedLocations}
                            placeholder="Choose Location"
                            getOptionValue={(e) => `${e.value}-${e.type}`}
                            getOptionLabel={(e) => e.label}
                        />
                    </Form.Group>
                </div>

                {/* Indicator list */}
                {stackedIndicators.map((ind, idx) => (
                    <div key={idx} className="mb-3 p-3 border rounded">
                        <Form.Label>Indicator {idx + 1}</Form.Label>
                        <div className="row align-items-center">
                            <div className="col-md-5">
                                <Form.Group>
                                    <Select
                                        options={indicators.filter(
                                            (f) => f.measurement_type !== "Categorical - Nominal - Text" && Array.isArray(f.sub_indicators) && f.sub_indicators.length > 0
                                        )
                                            .map(f => ({
                                                value: f.indicator_code,
                                                label: `${f.indicator_name} (${f.indicator_date})`,
                                                subIndicators: f.sub_indicators || [],
                                            }))}
                                        onChange={(opt) => handleStackedIndicatorChange(idx, opt)}
                                        value={ind.colorIndicator}
                                        classNamePrefix="select"
                                        placeholder="Choose Indicator"
                                        noOptionsMessage={noOptionsMessage}
                                    />
                                </Form.Group>
                            </div>

                            <div className="col-md-2 d-flex justify-content-end ">
                                {ind.colorIndicator && (
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleRemoveStackedIndicator(idx)}
                                        title="Remove Indicator"
                                    >
                                        <MdOutlineDeleteOutline />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}

                {/* Add button (disabled after 4 indicators) */}
                <Button
                    variant="outline-primary"
                    onClick={handleAddStackedIndicator}
                    disabled={stackedIndicators.length >= 1}
                >
                    + Add Indicator
                </Button>

                <div className="row mt-2">
                    <div className="col-md-8 text-start"><p style={{ 'font-size': '0.85em' }}> <i>Note:</i> Indicators added to the workspace are listed in the dropdown list. To include additional indicators, go to the Home page and add the required indicators to the workspace.</p></div>
                    <div className="text-end col-md-4 gap-2">
                        <Button variant="secondary" onClick={handleStackedReset} className="btn-sm">
                            Reset
                        </Button>&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button
                            onClick={handleStackedSubmit}
                            className="btn-sm btn-primary"
                            disabled={isStackedSubmitDisabled}
                        >
                            Submit
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StackedVisualization;